import { useState, useEffect } from 'react';
import api from '../lib/api.js';
import { useApi } from '../hooks/useApi.js';

export default function ClientForm({ clientId, onSaved, onCancel }) {
  const isEdit = !!clientId;
  const { data: initial, loading: loadingInitial } = useApi(isEdit ? '/clients/' + clientId : null);
  const blank = {
    client_type: 'individual', legal_name: '', first_name: '', last_name: '', tax_code: '', vat_number: '',
    email: '', pec: '', phone: '', mobile: '', sdi_code: '0000000', recipient_code: '', street: '', city: '',
    province: '', zip: '', country: 'Italia', contact_person: '', website: '', status: 'lead', source: '', notes: '', tag_names: [],
  };
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { if (initial?.data) setForm({ ...blank, ...initial.data, tag_names: initial.data.tags?.map((t) => t.name) ?? [] }); }, [initial]);
  const set = (f, v) => setForm((p) => ({ ...p, [f]: v }));

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.legal_name?.trim()) { setError('Ragione sociale / Nome obbligatorio'); return; }
    setSaving(true); setError('');
    try {
      const payload = { ...form, tag_names: form.tag_names.filter(Boolean) };
      const res = isEdit ? await api.patch('/clients/' + clientId, payload) : await api.post('/clients', payload);
      if (res.ok) onSaved?.(res.data); else setError(res.error || 'Errore salvataggio');
    } catch (err) { setError(err?.message || 'Errore di rete'); }
    finally { setSaving(false); }
  }

  if (loadingInitial && isEdit) return <div className="text-surface-4 py-8 text-center">Caricamento...</div>;

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-surface">{isEdit ? 'Modifica Cliente' : 'Nuovo Cliente'}</h2>
        <button type="button" className="btn btn-ghost" onClick={onCancel}>✕</button>
      </div>
      {error && <div className="p-3 rounded-md bg-danger/10 text-danger text-sm border border-danger/30">{error}</div>}
      <section>
        <h3 className="text-xs uppercase tracking-wide text-surface-4 mb-3">Anagrafica</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <label className="flex flex-col gap-1 text-sm"><span className="text-surface-4">Ragione sociale *</span><input className="input" value={form.legal_name} onChange={(e) => set('legal_name', e.target.value)} required /></label>
          <label className="flex flex-col gap-1 text-sm"><span className="text-surface-4">Tipo</span><select className="input" value={form.client_type} onChange={(e) => set('client_type', e.target.value)}><option value="individual">Persona fisica</option><option value="company">Azienda</option></select></label>
          <label className="flex flex-col gap-1 text-sm"><span className="text-surface-4">Nome</span><input className="input" value={form.first_name} onChange={(e) => set('first_name', e.target.value)} /></label>
          <label className="flex flex-col gap-1 text-sm"><span className="text-surface-4">Cognome</span><input className="input" value={form.last_name} onChange={(e) => set('last_name', e.target.value)} /></label>
          <label className="flex flex-col gap-1 text-sm"><span className="text-surface-4">Stato</span><select className="input" value={form.status} onChange={(e) => set('status', e.target.value)}><option value="lead">Lead</option><option value="active">Attivo</option><option value="inactive">Inattivo</option><option value="archived">Archiviato</option></select></label>
          <label className="flex flex-col gap-1 text-sm"><span className="text-surface-4">Fonte</span><input className="input" value={form.source} onChange={(e) => set('source', e.target.value)} /></label>
        </div>
      </section>
      <section>
        <h3 className="text-xs uppercase tracking-wide text-surface-4 mb-3">Fiscale & Contatti</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <label className="flex flex-col gap-1 text-sm"><span className="text-surface-4">P.IVA</span><input className="input" value={form.vat_number} onChange={(e) => set('vat_number', e.target.value)} /></label>
          <label className="flex flex-col gap-1 text-sm"><span className="text-surface-4">Cod. Fiscale</span><input className="input" value={form.tax_code} onChange={(e) => set('tax_code', e.target.value)} /></label>
          <label className="flex flex-col gap-1 text-sm"><span className="text-surface-4">Email</span><input className="input" type="email" value={form.email} onChange={(e) => set('email', e.target.value)} /></label>
          <label className="flex flex-col gap-1 text-sm"><span className="text-surface-4">PEC</span><input className="input" value={form.pec} onChange={(e) => set('pec', e.target.value)} /></label>
          <label className="flex flex-col gap-1 text-sm"><span className="text-surface-4">Telefono</span><input className="input" value={form.phone} onChange={(e) => set('phone', e.target.value)} /></label>
          <label className="flex flex-col gap-1 text-sm"><span className="text-surface-4">Cellulare</span><input className="input" value={form.mobile} onChange={(e) => set('mobile', e.target.value)} /></label>
          <label className="flex flex-col gap-1 text-sm"><span className="text-surface-4">SdI Code</span><input className="input" value={form.sdi_code} onChange={(e) => set('sdi_code', e.target.value)} /></label>
          <label className="flex flex-col gap-1 text-sm"><span className="text-surface-4">Website</span><input className="input" value={form.website} onChange={(e) => set('website', e.target.value)} /></label>
        </div>
      </section>
      <ClientFormExtra form={form} set={set} />
      <div className="flex items-center gap-3 pt-2">
        <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Salvataggio...' : isEdit ? 'Salva modifiche' : 'Crea cliente'}</button>
        <button type="button" className="btn btn-ghost" onClick={onCancel}>Annulla</button>
      </div>
    </form>
  );
}
/** TagSelector — checkbox multiplo per tag */
function TagSelector({ current, onChange }) {
  const { data: tags, loading } = useApi('/clients/tags');
  if (loading) return <span className="text-surface-4 text-sm">Caricamento tag...</span>;
  const allTags = tags?.data ?? [];
  function toggle(name) {
    const next = current.includes(name) ? current.filter((n) => n !== name) : [...current, name];
    onChange(next);
  }
  return (
    <div className="flex flex-wrap gap-2">
      {allTags.map((t) => {
        const active = current.includes(t.name);
        return (
          <button key={t.id} type="button" onClick={() => toggle(t.name)}
            className={'badge cursor-pointer ' + (active ? 'bg-brand-600/20 text-brand-300 border-brand-500/40' : 'bg-line text-surface-4 border-line')}>
            {t.name}{current.includes(t.name) && <span className="ml-1">✓</span>}
          </button>
        );
      })}
    </div>
  );
}

/** Sezione extra del form: indirizzo, tag, note */
export function ClientFormExtra({ form, set }) {
  return (
    <>
      <section>
        <h3 className="text-xs uppercase tracking-wide text-surface-4 mb-3">Indirizzo</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <label className="flex flex-col gap-1 text-sm md:col-span-2"><span className="text-surface-4">Via / Piazza</span><input className="input" value={form.street} onChange={(e) => set('street', e.target.value)} /></label>
          <label className="flex flex-col gap-1 text-sm"><span className="text-surface-4">CAP</span><input className="input" value={form.zip} onChange={(e) => set('zip', e.target.value)} /></label>
          <label className="flex flex-col gap-1 text-sm"><span className="text-surface-4">Città</span><input className="input" value={form.city} onChange={(e) => set('city', e.target.value)} /></label>
          <label className="flex flex-col gap-1 text-sm"><span className="text-surface-4">Provincia</span><input className="input" value={form.province} onChange={(e) => set('province', e.target.value)} placeholder="MI" /></label>
          <label className="flex flex-col gap-1 text-sm"><span className="text-surface-4">Nazione</span><input className="input" value={form.country} onChange={(e) => set('country', e.target.value)} /></label>
        </div>
      </section>
      <section>
        <h3 className="text-xs uppercase tracking-wide text-surface-4 mb-3">Tag</h3>
        <TagSelector current={form.tag_names} onChange={(names) => set('tag_names', names)} />
      </section>
      <section>
        <h3 className="text-xs uppercase tracking-wide text-surface-4 mb-3">Note</h3>
        <textarea className="input min-h-[80px] resize-y" value={form.notes || ''} onChange={(e) => set('notes', e.target.value)} placeholder="Note interne sul cliente..." />
      </section>
    </>
  );
}
